import { DiContainer } from '@jack-henry/frontend-utils/di';
import { deepEquals, delay } from '@jack-henry/frontend-utils/functions';
import { Recordset } from '@treasury/FDL';
import { AlarmClock } from '@treasury/alarm-clock';
import { AnalyticsEvent, AnalyticsService } from '@treasury/core/analytics';
import { CheckException } from '@treasury/domain/arp';
import { CheckExceptionsService } from '@treasury/domain/channel/services/positive-pay/check-exceptions-service';
import { CheckExceptionAggregate } from '@treasury/domain/channel/services/positive-pay/entities/check-exception-aggregate';
import { CheckExceptionReturnReason } from '@treasury/domain/channel/services/positive-pay/entities/check-exception-return-reason';
import { PositivePayOption } from '@treasury/domain/channel/services/positive-pay/entities/positive-pay-option';
import {
    ACCEPTED_ATTACHMENT_EXTENSIONS,
    CHECK_EXCEPTION_CUTOFF,
    CHECK_EXCEPTION_START,
    RETURN_REASON_OPTION,
} from '@treasury/domain/channel/types/arp/constants.js';
import { Feature, FeatureFlagService } from '@treasury/domain/services/feature-flags';
import { ListeningElementMixin } from '@treasury/omega/components';
import { openDialog } from '@treasury/omega/components/dialog';
import '@treasury/omega/components/omega-file-upload.js';
import '@treasury/omega/components/omega-table';
import { LitElement, css, html, nothing } from 'lit';
import '../../../../../components/blocking-loader.js';
import checkExceptionFields from '../../../../../positive-pay/check-exceptions/data/check-exception-fields.js';
import { setColumns } from '../../../../../positive-pay/check-exceptions/data/table-columns.js';
import { exceptionWidgetStyles } from '../styles.js';

export class CheckExceptionWidget extends ListeningElementMixin(LitElement) {
    /**
     * @type {CheckExceptionsService}
     */
    checkExceptionService;

    /**
     * @type {AnalyticsService}
     */
    analyticsService;

    /**
     * @type {FeatureFlagService}
     */
    featureFlagService;

    fieldNames;

    /**
     * `DefaultReturnReason` is the unique id of the default {@link CheckExceptionReturnReason}`
     * Is set in the firstUpdated method to the first return reason in the
     * returnReasons array that has isDefault set to true
     *
     * Business Rule:
     * When the default return reason is set, the 'Accept Decision' button will be shown
     * for that return reason as long as the record is not protected and the records
     * return reason unique id matches the default return reason
     *
     * @type {string}
     */
    defaultReturnReason;

    static get properties() {
        return {
            actions: Object,
            /**
             * @type {CheckExceptionAggregate}
             */
            records: Object,
            recordset: Object,
            updatedRecordset: Object,
            isReviewStep: Boolean,
            reviewAll: Boolean,
            isDetailView: Boolean,
            isAfterCutoff: { state: true, type: Boolean },
            alert: Object,
            saving: Boolean,
            /**
             * @type {CheckExceptionReturnReason[]}
             */
            returnReasons: Array,
            /**
             * @type { PositivePayOption[] }
             */
            options: Array,
            loadingImages: Boolean,
            _hasAttachmentEntitlement: { state: true, type: Boolean },
            /**
             * Boolean that is toggled when the user clicks the "Review All" button
             * @type {boolean}
             */
            reviewAll: { state: true, type: Boolean },
        };
    }

    constructor() {
        super();
        this.actions = {
            openCommentDialog: async record => this.handleCommentClick(record),
            detailView: record => this.showDetailView(record),
            acceptDefaultDecision: record => this.handleDefaultDecision(record),
        };
        this.alert = {
            title: '',
            message: '',
            visible: false,
            code: '',
        };
        this.isReviewStep = false;
        this.reviewAll = false;
        this.saving = false;
        this.isAfterCutoff = this.fiClock.isAfter(CHECK_EXCEPTION_CUTOFF);
        this.recordset = new Recordset({}, []);
        this.updatedRecordset = new Recordset({}, []);
    }

    get fiClock() {
        return AlarmClock.getInstance();
    }

    get disableReview() {
        if (!this.updatedRecordset) return true;
        return this.updatedRecordset.allRecords.length === 0;
    }

    get disableReviewAll() {
        if (!this.recordset) return true;
        return this.recordset.allRecords.length === 0;
    }

    /**
     * Handles triggering the conformation dialog when the user clicks the "Accept Decision" button
     * @param {*} record
     */
    async handleDefaultDecision(record) {
        // Manually remove any other dialogs that may be open
        const dialog = document.querySelector('omega-dialog');
        if (dialog) {
            dialog.remove();
        }
        const saveResult = await openDialog({
            title: 'Accept Decision',
            content: html`<div>
                <p>Are you sure you want to decision this check exception?</p>
            </div>`,
            actions: resolve => html`
                <omega-button
                    type="primary"
                    data-testid="confirm-acceptance-of-default-decision-button"
                    @click=${() => resolve('confirm')}
                >
                    Decision
                </omega-button>
            `,
        });
        if (saveResult === 'confirm') {
            this.processDefaultDecision(record);
        }
    }

    /**
     * Handles triggering the comment dialog when the user clicks the "Comment" button
     * @param {*} record
     */
    async handleCommentClick(record) {
        // Manually remove any other dialogs that may be open
        const dialog = document.querySelector('omega-dialog');
        if (dialog) {
            dialog.remove();
        }
        const saveResult = await openDialog({
            title: 'Comment',
            content: this.renderCommentDialog(record),
            actions: resolve => html`
                <omega-button
                    type="primary"
                    .loading=${this.isSavingExceptions}
                    data-testid="comment-confirm-button"
                    @click=${() => resolve('confirm')}
                >
                    Done
                </omega-button>
            `,
        });
        if (saveResult === 'confirm') {
            this.saveAttachmentAndComment(record);
        }
    }

    connectedCallback() {
        super.connectedCallback();
        this.listenTo(this.fiClock, CHECK_EXCEPTION_START, () => this.requestUpdate());
        this.listenTo(this.fiClock, CHECK_EXCEPTION_CUTOFF, async () => {
            this.isAfterCutoff = true;
            this.isReviewStep = false;
            this.dispatchEvent(
                new CustomEvent('recordUpdateRequest', {
                    bubbles: true,
                    detail: true,
                })
            );
            await delay(100);
            this.recordset.allRecords.forEach(record => {
                record.setField('isPastCutOffTime', true);
            });
            this.columns = [
                ...setColumns(true, this.hasReturnReason, false, true),
                {
                    label: 'Actions',
                    type: 'actions',
                    actions: [
                        {
                            label: 'Details',
                            action: 'detailView',
                            visibleWhen: () => true,
                        },
                    ],
                },
            ];
            this.requestUpdate();
        });
    }

    async firstUpdated() {
        try {
            const di = await DiContainer.getInstance();
            this.checkExceptionService = di.get(CheckExceptionsService);
            this.analyticsService = di.get(AnalyticsService);
            this.featureFlagService = di.get(FeatureFlagService);
            this._hasAttachmentEntitlement = await this.featureFlagService.isEnabled(
                Feature.PositivePayCheckExceptionAttachments
            );
            const returnReasonFeatureEnabled = await this.featureFlagService.isEnabled(
                Feature.PositivePayReturnReason
            );
            this.hasReturnReason =
                this.hasOption(RETURN_REASON_OPTION) && returnReasonFeatureEnabled;
            this.fields = checkExceptionFields(
                this.returnReasons,
                this.isAfterCutoff,
                this.hasReturnReason
            );

            /**
             * @type {string}
             */
            let defaultReturnReason;
            if (this.returnReasons && this.returnReasons.length > 0) {
                defaultReturnReason = this.returnReasons.find(
                    e => e.isDefault === true
                ).returnReasonUniqueId;
            }

            this.columns = [
                ...setColumns(this.isAfterCutoff, this.hasReturnReason, false, true),
                {
                    label: 'Actions',
                    type: 'actions',
                    actions: [
                        {
                            label: 'Details',
                            action: 'detailView',
                            visibleWhen: () => true,
                        },
                        {
                            label: 'Accept Decision',
                            action: 'acceptDefaultDecision',
                            visibleWhen: record => {
                                /**
                                 * @type {boolean}
                                 */
                                const protectedRecord = record.getField('isProtected');
                                /**
                                 * @type {string}
                                 */
                                const returnReason = record.getField('returnReason');
                                defaultReturnReason =
                                    this.defaultReturnReason || defaultReturnReason;
                                const isVisible =
                                    protectedRecord === false &&
                                    returnReason === defaultReturnReason;
                                return isVisible;
                            },
                        },
                    ],
                },
            ];
            this.reviewColumns = setColumns(true, this.hasReturnReason);
            this.recordset = await new Recordset(this.fields, async () => this.records?.data);
            this.updatedRecordset = await new Recordset(this.fields, async () => []);
            this.fieldNames = Object.keys(this.fields);
        } catch (e) {
            console.error(e);
            this.requestUpdate();
        }

        const recordset = this.recordset;
        this.listenTo(this.recordset, 'updated', () => {
            if (this.reviewAll) {
                // Only review the records that are not protected
                // protected records cannot be decisioned
                const validRecordsToDecision = recordset.allRecords.filter(
                    record => record.getField('isProtected') === false
                );

                this.updatedRecordset.setData(validRecordsToDecision.map(record => record.values));
                return;
            }
            const updatedData = this.getDataForUpdatedRecordset();
            this.updatedRecordset.setData(updatedData);
            this.recordset.requestUpdate();
            this.requestUpdate();
        });
        await this.recordset.requestUpdate();
        this.requestUpdate();
    }

    handleReviewAllClick() {
        this.reviewAll = true;
        this.toggleReviewStep();
    }

    getDataForUpdatedRecordset() {
        const updatedData = [];
        this.recordset.allRecords.forEach(record => {
            try {
                // If the decision choice is Pay make sure we return out any return reason
                // This is because return reasons are only allowed for return decisions
                if (
                    record.getField('decisionChoice') === 'Pay' &&
                    record.getField('returnReason') !== null
                ) {
                    record.values.returnReason = null;
                    //record.setField('returnReason', null);
                }
                if (
                    // Check if any field (excluding 'step') has been modified
                    this.fieldNames !== undefined &&
                    this.fieldNames.some(key => {
                        const value = record.getField(key);
                        const initialValue = record.initialValues[key];
                        // Ignore changes to the 'step' field
                        if (key !== 'step') {
                            // Return true if the current value is different from the initial value
                            return !deepEquals(value, initialValue);
                        }
                        // If the key is 'step', consider it unchanged for this check
                        return false;
                    })
                ) {
                    updatedData.push(record.values);
                }
            } catch (e) {
                console.error(e);
                throw e;
            }
        });

        return updatedData;
    }
    updated(changedProperties) {
        if (
            (changedProperties.has('records') || changedProperties.has('returnReasons')) &&
            this.returnReasons &&
            this.returnReasons.length
        ) {
            this.defaultReturnReason = this.returnReasons.find(
                e => e.isDefault === true
            ).returnReasonUniqueId;
        }

        if (changedProperties.has('records')) {
            this.recordset = new Recordset(this.fields, () => this.records?.data);
        }

        // Once you move to the review step, the review all button should be reset to false
        if (changedProperties.has('isReviewStep') && this.isReviewStep) {
            this.reviewAll = false;
        }
    }

    async saveAttachmentAndComment(record) {
        try {
            const result = await this.checkExceptionService.saveAttachmentAndComment(
                new CheckException(record.values),
                record.getField('comment'),
                record.getField('fileToUpload')
            );
            if (!result) {
                throw new Error('Failed to save attachment and comment');
            }

            this.handleAlert(
                {
                    ...this.alert,
                    visible: true,
                    message: 'Comment saved successfully!',
                    type: 'success',
                },
                5000
            );
            if (record.getField('fileToUpload')) {
                record.setField('attachmentFileName', record.getField('fileToUpload').name);
            }
        } catch (e) {
            console.error(e);
            this.handleAlert(
                {
                    ...this.alert,
                    visible: true,
                    message: e.message,
                    code: e.code,
                    type: 'error',
                },
                5000
            );
        }
    }

    async getCheckExceptionAttachment(arpExceptionDetailUniqueId) {
        await this.checkExceptionService.getCheckExceptionAttachment(arpExceptionDetailUniqueId);
    }

    async processDefaultDecision(record) {
        try {
            this.handleAlert({
                ...this.alert,
                message: html`<omega-progress card></omega-progress>`,
                actions: nothing,
            });

            // Create a new CheckException entity from the record's values.
            // This allows us to use the domain logic for making decisions.
            // The `record.values` likely contains the raw data for a check exception,
            // including a `defaultDecisionChoice` field.

            // The `CheckException` class encapsulates the logic
            // for handling decisions.
            // Calling `.pay()` or `.return()` updates the decision status of the check exception.
            // These methods (defined in `check-exception.entity.ts`)
            // set internal flags (`_decisionPay`, `_decisionReturn`) and update the
            // `decisionStatus` on the underlying DTO. They also handle the `decisionModified` state.
            // This ensures the correct decision is recorded and the object is ready for submission.
            const checkException = new CheckException(record.values);
            if (record.values.defaultDecisionChoice === 'Pay') {
                checkException.pay();
            } else if (record.values.defaultDecisionChoice === 'Return') {
                checkException.return();
            }

            // The `saveCheckExceptions` method (defined in `check-exceptions-service.ts`)
            // prepares the data for submission and sends it to the server.
            // It uses the `CheckException` DTO, which is now ready for submission.
            await this.checkExceptionService.saveCheckExceptions([checkException.toDto()]);

            this.handleAlert(
                {
                    ...this.alert,
                    visible: true,
                    message: 'Decision saved successfully!',
                    type: 'success',
                    actions: html`<div>
                        <omega-button
                            @click=${() => {
                                this.alert = { ...this.alert, visible: false };
                                this.recordset.requestUpdate();
                            }}
                            >Close</omega-button
                        >
                    </div>`,
                },
                2000
            );
        } catch (e) {
            this.handleAlert({
                ...this.alert,
                visible: true,
                message: e.message,
                type: 'error',
            });
        } finally {
            this.dispatchEvent(
                new CustomEvent('recordUpdateRequest', {
                    bubbles: true,
                    detail: true,
                })
            );
        }
    }

    /**
     *
     * @param {AlertOptions} alert
     * @param {number | undefined} duration
     */
    handleAlert(alert, duration) {
        this.dispatchEvent(
            new CustomEvent('alert', {
                bubbles: true,
                detail: alert,
            })
        );
        if (duration) {
            window.setTimeout(() => {
                this.dispatchEvent(
                    new CustomEvent('alert', {
                        bubbles: true,
                        detail: { ...alert, visible: false },
                    })
                );
            }, duration);
        }
    }

    async save() {
        this.saving = true;
        this.handleAlert({
            ...this.alert,
            visible: true,
            message: 'Saving decisions...',
            type: 'time-sensitive',
        });
        try {
            const values = this.updatedRecordset.allRecords.map(record => record.values);
            const totalAmount = values.reduce(
                // eslint-disable-next-line no-return-assign
                (total, exception) => (total += exception.paidAmount || 0),
                0
            );

            await this.checkExceptionService.saveCheckExceptions(values);

            this.analyticsService.track(AnalyticsEvent.CheckExceptionReview, {
                itemCount: values.length,
                totalAmount,
            });

            this.dispatchEvent(
                new CustomEvent('recordUpdateRequest', {
                    bubbles: true,
                    detail: true,
                })
            );
            this.toggleReviewStep();
            this.handleAlert(
                {
                    ...this.alert,
                    visible: true,
                    message: 'Decisions saved successfully!',
                    type: 'success',
                },
                2000
            );
        } catch (e) {
            console.error(e);
            this.handleAlert(
                {
                    ...this.alert,
                    visible: true,
                    message: 'There was an error saving the decisions. Please try again.',
                    type: 'error',
                },
                2000
            );
        } finally {
            this.saving = false;
        }
    }

    /**
     * @param {string} optionName Object containing the name and value properties of the option to check.
     * @returns {boolean}
     */
    hasOption(optionName) {
        if (!this.options) return false;
        const foundOption = this.options.find(option => option.name === optionName);
        return foundOption && foundOption.value === true;
    }

    handleActions({ action, record, rowIndex }) {
        this.actions[action](record, rowIndex);
    }

    toggleReviewStep() {
        this.isReviewStep = !this.isReviewStep;
        const stepCount = this.isReviewStep ? 1 : 0;
        this.recordset.allRecords.forEach(record => record.setField('step', stepCount));

        this.dispatchEvent(
            new CustomEvent('setReviewStep', {
                bubbles: true,
                detail: this.isReviewStep,
            })
        );
    }

    showDetailView(record) {
        this.isDetailView = true;
        this.detailRecord = record;
    }

    closeDetailView() {
        this.isDetailView = false;
    }

    renderBlockingLoader() {
        return this.loadingImages ? html`<blocking-loader></blocking-loader>` : nothing;
    }

    renderFileControl(record) {
        if (!this._hasAttachmentEntitlement) return nothing;

        const fileName = record.getField('attachmentFileName');
        return fileName
            ? html`<omega-button
                  type="icon"
                  icon="download"
                  @click=${() =>
                      this.getCheckExceptionAttachment(record.values.arpExceptionDetailUniqueId)}
              >
                  ${fileName}
              </omega-button>`
            : html` <omega-file-upload
                  icon="true"
                  buttonText="Add Attachment"
                  buttonIcon="paperclip"
                  .accepted=${ACCEPTED_ATTACHMENT_EXTENSIONS}
                  @filesUploaded=${e => {
                      record.setField('fileToUpload', e.detail.files[0]);
                  }}
              ></omega-file-upload>`;
    }

    renderCommentDialog(record) {
        return html`
            <div style="padding: 0 10px;">
                <omega-field field="comment" label="Comment" .record=${record}></omega-field>
                ${this.renderFileControl(record)}
            </div>
        `;
    }

    renderOutsideCutoffTime(message) {
        return html`
            <div class="outside-cutoff-time">
                <p class="message">${message}</p>
            </div>
        `;
    }

    renderAlert() {
        if (!this.alert.visible) return nothing;
        const { code, time, message, type, title, actions, posture, visible } = this.alert;
        const renderedCode = code ? html`${code}: ` : nothing;
        const renderedTime = time ? html`<br />Time: ${time}` : nothing;

        return html`
            <div class="alert-wrapper">
                <omega-alert
                    type=${type}
                    title=${title}
                    posture=${posture}
                    ?isVisible=${visible}
                    @close=${() => {
                        this.alert = { ...this.alert, visible: false };
                    }}
                >
                    ${renderedCode} ${message} ${renderedTime} ${actions}
                </omega-alert>
            </div>
        `;
    }

    renderDetailView() {
        return html`
            <div class="details-header">
                <span class="title">
                    <h3>Details ${this.detailRecord.getField('accountNumber')}</h3>
                </span>
                <omega-button
                    type="icon"
                    icon="close"
                    hideLabel="always"
                    @click=${this.closeDetailView}
                ></omega-button>
            </div>
            <div class="details-body omega-flex-form">
                <div class="form-column">
                    <omega-field field="accountNumber" .record=${this.detailRecord}></omega-field>
                    <omega-field field="checkNumber" .record=${this.detailRecord}></omega-field>
                    <omega-field field="paidAmount" .record=${this.detailRecord}></omega-field>
                    <omega-field field="issuedAmount" .record=${this.detailRecord}></omega-field>
                    <omega-field field="postedDate" .record=${this.detailRecord}></omega-field>
                    <omega-field field="issuedDate" .record=${this.detailRecord}></omega-field>
                    <omega-field field="issuedPayee" .record=${this.detailRecord}></omega-field>
                </div>
                <div class="form-column">
                    <omega-field
                        field="returnReasonDescription"
                        .record=${this.detailRecord}
                    ></omega-field>
                    <omega-field field="exceptionReason" .record=${this.detailRecord}></omega-field>
                    <omega-field
                        field="lastDecisionTakenBy"
                        .record=${this.detailRecord}
                    ></omega-field>
                    <omega-field field="decisionStatus" .record=${this.detailRecord}></omega-field>
                    <omega-field field="sourceOfEntry" .record=${this.detailRecord}></omega-field>
                    <omega-field field="protected" .record=${this.detailRecord}></omega-field>
                    <omega-field field="ddaBatchNumber" .record=${this.detailRecord}></omega-field>
                    <omega-field
                        field="ddaSequenceNumber"
                        .record=${this.detailRecord}
                    ></omega-field>
                </div>
                <div class="form-column">
                    <omega-field field="audits" .record=${this.detailRecord}></omega-field>
                </div>
            </div>
        `;
    }

    renderReviewTable() {
        return html`
            <omega-table
                .columnDefinitions=${this.reviewColumns}
                .recordset=${this.updatedRecordset}
                @action=${e => this.handleActions(e.detail)}
            ></omega-table>
            <omega-button-bar alignment="left" position="relative">
                <omega-button
                    type="primary"
                    @click=${this.save}
                    ?disabled=${this.saving}
                    data-testid="save-button"
                >
                    Decision (${this.updatedRecordset.totalCount})
                </omega-button>
                <omega-button
                    type="secondary"
                    @click=${this.toggleReviewStep}
                    ?disabled=${this.saving}
                    data-testid="back-button"
                >
                    Back
                </omega-button>
            </omega-button-bar>
        `;
    }

    renderTable() {
        if (!this.recordset || !this.columns)
            return html`<em>No Check Exceptions Found or Columns were not correctly set</em>`;
        return html`
            <omega-table
                .columnDefinitions=${this.columns}
                .recordset=${this.recordset}
                @action=${e => this.handleActions(e.detail)}
                @loadingCheckImage=${e => {
                    this.loadingImages = e.detail;
                }}
            ></omega-table>
            ${this.renderReviewButton()}
        `;
    }

    renderReviewButton() {
        if (this.isAfterCutoff) return nothing;
        return html`<div class="review-button-container">
            <omega-button-bar alignment="left" position="relative">
                <omega-button
                    type="approve"
                    data-testid="review-button"
                    @click=${this.toggleReviewStep}
                    .disabled=${this.disableReview}
                    data-testid="review-button"
                >
                    Review
                </omega-button>
                <omega-button
                    type="primary"
                    .disabled=${this.disableReviewAll}
                    @click=${this.handleReviewAllClick}
                    data-testid="review-all-button"
                    >Review All</omega-button
                >
                <omega-button
                    type="secondary"
                    .disabled=${this.isSavingExceptions ||
                    !this.updatedRecordset ||
                    this.updatedRecordset.totalCount === 0}
                    @click=${() => {
                        this.updatedRecordset = new Recordset(this.fields, []);
                        this.updatedRecordset.requestSoftUpdate();
                        this.dispatchEvent(new CustomEvent('recordUpdateRequest'));
                    }}
                    data-testid="reset-button"
                    >Reset</omega-button
                >
            </omega-button-bar>
        </div>`;
    }

    renderBody() {
        if (this.isReviewStep) return this.renderReviewTable();
        if (this.isDetailView) return this.renderDetailView();
        return this.renderTable();
    }

    render() {
        if (this.fiClock.isBefore(CHECK_EXCEPTION_START)) {
            return this.renderOutsideCutoffTime(
                `Check exception items are not available at this time. Please try again after ${this.fiClock.getAlarm(
                    CHECK_EXCEPTION_START
                )} (${this.fiClock.timeZone}).`
            );
        }
        if (this.fiClock.isAfter(CHECK_EXCEPTION_CUTOFF)) {
            return html`${this.renderOutsideCutoffTime(
                html`Check exception items are disabled because the current time is past the
                ${this.fiClock.getAlarm(CHECK_EXCEPTION_CUTOFF)} Cut-Off time
                (${this.fiClock.timeZone})`
            )}${this.renderBody()}`;
        }

        if (!this.recordset || this.recordset.allRecords.length < 1) {
            return html`<em>No Check Exceptions Found</em>`;
        }

        return html`${this.renderBlockingLoader()} ${this.renderAlert()} ${this.renderBody()}`;
    }

    // eslint-disable-next-line @treasury/style-includes-host-display
    static get styles() {
        return [
            exceptionWidgetStyles,
            css`
                :host {
                    display: flex;
                }
                omega-table {
                    min-height: 280px;
                    max-height: 350px;
                }
                .details-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    border-bottom: 1px solid var(--omega-secondary-lighten-300);
                    padding: 0px 15px;
                    height: 45px;
                }
                .details-header h3 {
                    margin: 0;
                    padding: 0;
                }
                .details-body {
                    padding: 0px 15px;
                    overflow: auto;
                }
                .omega-flex-form {
                    display: flex;
                }
                .form-column {
                    flex: 1 1 0;
                    margin: 10px;
                }
                .form-column:not(:last-of-type) {
                    padding-right: 10px;
                    border-right: 1px solid #d3d6d9;
                }
                .omega-flex-form omega-field {
                    --omega-field-control-width: 120px;
                    margin: 5px;
                    min-height: 32px;
                }
            `,
        ];
    }
}
window.customElements.define('check-exception-widget', CheckExceptionWidget);
export default CheckExceptionWidget;
